import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Marketing Categories Component
const MarketingCategoriesSection = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const categories = {
    core: {
      title: 'Core Marketing',
      color: 'green',
      items: [
        { name: 'Traditional Marketing', path: '/traditional-marketing' },
        { name: 'Digital Marketing', path: '/digital-marketing' },
        { name: 'Inbound Marketing', path: '/inbound-marketing' },
        { name: 'Outbound Marketing', path: '/outbound-marketing' },
        { name: 'Relationship Marketing', path: '/relationship-marketing' },
        { name: 'Event Marketing', path: '/event-marketing' },
        { name: 'Guerrilla Marketing', path: '/guerrilla-marketing' }
      ]
    },
    digital: {
      title: 'Digital Marketing',
      color: 'yellow',
      items: [
        { name: 'Content Marketing', path: '/content-marketing' },
        { name: 'Social Media Marketing', path: '/social-media-marketing' },
        { name: 'Affiliate Marketing', path: '/affiliate-marketing' },
        { name: 'Franchisee Marketing', path: '/franchisee-marketing' },
        { name: 'Email Marketing', path: '/email-marketing' },
        { name: 'Search Engine Marketing (SEM)', path: '/search-engine-marketing' },
        { name: 'Search Engine Optimization (SEO)', path: '/search-engine-optimization' },
        { name: 'Influencer Marketing', path: '/influencer-marketing' },
        { name: 'Mobile Marketing', path: '/mobile-marketing' },
        { name: 'Video Marketing', path: '/video-marketing' },
        { name: 'Podcast Marketing', path: '/podcast-marketing' }
      ]
    },
    strategic: {
      title: 'Strategic Marketing',
      color: 'blue',
      items: [
        { name: 'B2B Marketing (Business-to-Business)', path: '/b2b-marketing' },
        { name: 'B2C Marketing (Business-to-Consumer)', path: '/b2c-marketing' },
        { name: 'C2C Marketing (Consumer-to-Consumer)', path: '/c2c-marketing' },
        { name: 'Niche Marketing', path: '/niche-marketing' },
        { name: 'Mass Marketing', path: '/mass-marketing' },
        { name: 'Personalized Marketing', path: '/personalized-marketing' }
      ]
    },
    advanced: {
      title: 'Modern Marketing',
      color: 'red',
      items: [
        { name: 'Brand Marketing', path: '/brand-marketing' },
        { name: 'Performance Marketing', path: '/performance-marketing' },
        { name: 'Cause/CSR Marketing', path: '/cause-csr-marketing' },
        { name: 'Experiential Marketing', path: '/experiential-marketing' },
        { name: 'Viral Marketing', path: '/viral-marketing' },
        { name: 'Account-Based Marketing (ABM)', path: '/account-based-marketing' }
      ]
    }
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategory(expandedCategory === categoryKey ? null : categoryKey);
  };

  const handleItemClick = (path) => {
    // Navigate to the specified path
    window.location.href = path;
  };

  const getGradientClass = (color) => {
    const gradients = {
      green: 'from-green-500 to-green-700',
      yellow: 'from-yellow-400 to-yellow-600',
      blue: 'from-blue-400 to-blue-600',
      red: 'from-red-500 to-red-700'
    };
    return gradients[color];
  };

  return (
    <section className="relative px-4 sm:px-8 lg:px-24 py-8 bg-gradient-to-br from-black/20 to-orange-400/50">
      <div className="max-w-7xl mx-auto relative z-10 mb-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Marketing Categories</h2>
          <p className="text-gray-300 text-lg">Explore different types of marketing strategies</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(categories).map(([key, category]) => (
            <div key={key}>
              <button
                onClick={() => toggleCategory(key)}
                className={`w-full bg-gradient-to-br ${getGradientClass(category.color)} rounded-2xl shadow-lg  transition-all duration-300 p-6 text-left `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{category.emoji}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-white drop-shadow-md">
                        {category.title}
                      </h3>
                    </div>
                  </div>
                  <div className="text-white">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {expandedCategory === key ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      )}
                    </svg>
                  </div>
                </div>
              </button>

              {expandedCategory === key && (
                <div className="mt-4 bg-gray-900 border-2 border-gray-700 rounded-xl shadow-md p-6">
                  <h4 className="text-xl font-semibold text-white mb-4 flex items-center">
                    <span className="mr-2">{category.emoji}</span>
                    {category.title} Strategies
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {category.items.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleItemClick(item.path)}
                        className="bg-gray-800 hover:bg-gray-700 rounded-lg p-4 transition-all duration-200 border border-gray-600 hover:border-orange-500 cursor-pointer text-left group"
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-orange-500 font-semibold text-sm mt-0.5 group-hover:text-orange-400">
                            {index + 1}.
                          </span>
                          <p className="text-gray-200 font-medium flex-1 group-hover:text-white">{item.name}</p>
                          <svg className="w-4 h-4 text-gray-500 group-hover:text-orange-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

function JoinAndEarn() {
  const navigate = useNavigate();

  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden w-full">
      {/* Navbar */}
      <Navbar />

      {/* Protection styles for images */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* Join & Earn Marketing Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 pt-20 pb-12 sm:py-20">
        <div className="max-w-7xl mx-auto">
          
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            
            {/* Left Side - Content */}
            <div className="space-y-8">
              
              {/* Main Heading */}
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Join & Earn with ISF Athena: Empower, Learn, and Grow Together
              </h2>

              {/* Description */}
              <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
                Transform lives through education, mentorship, and opportunity. Whether you're seeking guidance, ready to mentor, or eager to share your expertise, ISF Athena welcomes you to a thriving community where everyone grows together.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('../company_register')}
                  className="px-8 py-3 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition"
                >
                  Get Started Today
                </button>
                <button className="px-8 py-3 border-2 border-orange-500 text-orange-500 rounded-md font-medium hover:bg-orange-500 hover:text-white transition">
                  Explore Opportunities
                </button>
              </div>

            </div>

            {/* Right Side - Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="/Assests/home_page_89.png" 
                  alt="Join and Earn with ISF Athena" 
                  className="w-full h-[350px] object-cover"
                />
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Marketing Categories Section */}
      <MarketingCategoriesSection />

      {/* Section 1: Chapter 1 - Why ISF Athena */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-12 text-center">
            Why ISF Athena? Transforming Lives Through Mentorship, Learning, and Content
          </h2>

          {/* Three Cards Grid */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
            
            {/* Card 1 - Connect & Mentor */}
            <div className="border-2 border-orange-500 rounded-2xl p-8 space-y-4 bg-black/50">
              <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">
                Connect & Mentor
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                ISF Athena connects passionate mentors, driven students, and innovative content providers in a thriving ecosystem built on trust and collaboration.
              </p>
            </div>

            {/* Card 2 - Empower & Overcome */}
            <div className="border-2 border-orange-500 rounded-2xl p-8 space-y-4 bg-black/50">
              <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">
                Empower & Overcome
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Together, we empower foster youth and aspiring learners to overcome challenges and unlock their full potential through personalized support.
              </p>
            </div>

            {/* Card 3 - Grow & Earn */}
            <div className="border-2 border-orange-500 rounded-2xl p-8 space-y-4 bg-black/50">
              <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">
                Grow & Earn
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Join a community dedicated to education, leadership, and real-world impact — and earn rewards along the way as you contribute to lasting change.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Section 2: For Students - Your Journey to Success Starts Here */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            
            {/* Left Side - Content */}
            <div className="space-y-8">
              
              {/* Main Heading */}
              <h2 className="text-4xl lg:text-5xl font-bold text-orange-500 leading-tight">
                For Students: Your Journey to Success Starts Here
              </h2>

              {/* Subheading */}
              <h3 className="text-2xl lg:text-3xl font-bold text-white">
                Are You Ready to Transform Your Future?
              </h3>

              {/* Paragraph 1 */}
              <p className="text-gray-300 text-lg leading-relaxed">
                Are you a high school senior or graduate with foster care experience aiming for college or trade certification? Your dreams are closer than you think.
              </p>

              {/* Paragraph 2 */}
              <p className="text-gray-300 text-lg leading-relaxed">
                ISF Athena offers financial support, personalized mentoring, and leadership development tailored to your unique needs and aspirations.
              </p>

              {/* Paragraph 3 */}
              <p className="text-gray-300 text-lg leading-relaxed">
                Become a full-time student with confidence, backed by comprehensive resources that cover tuition, supplies, and life essentials so you can focus on what matters most — your education.
              </p>

            </div>

            {/* Right Side - Stats */}
            <div className="space-y-12">
              
              {/* Stat 1 - 100% Tuition Coverage */}
              <div className="text-center space-y-3">
                <div className="text-7xl lg:text-8xl font-bold text-orange-500">
                  100%
                </div>
                <h4 className="text-2xl font-bold text-white">
                  Tuition Coverage
                </h4>
                <p className="text-gray-300 text-base">
                  Full financial support for qualified students
                </p>
              </div>

              {/* Stat 2 - 1:1 Personal Mentoring */}
              <div className="text-center space-y-3">
                <div className="text-7xl lg:text-8xl font-bold text-orange-500">
                  1:1
                </div>
                <h4 className="text-2xl font-bold text-white">
                  Personal Mentoring
                </h4>
                <p className="text-gray-300 text-base">
                  Dedicated guidance from experienced professionals
                </p>
              </div>

              {/* Stat 3 - 24/7 Support Access */}
              <div className="text-center space-y-3">
                <div className="text-7xl lg:text-8xl font-bold text-orange-500">
                  24/7
                </div>
                <h4 className="text-2xl font-bold text-white">
                  Support Access
                </h4>
                <p className="text-gray-300 text-base">
                  Resources available whenever you need them
                </p>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* Section 3: Student Success Stories */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Top Heading */}
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-8">
            From Foster Care to College Success
          </h2>

          {/* Description */}
          <p className="text-gray-300 text-lg leading-relaxed mb-16 max-w-5xl">
            Every journey begins with a single step — and ISF Athena is here to walk beside you every step of the way. Discover how our comprehensive support system transforms challenges into opportunities for growth.
          </p>

          {/* Main Section Heading */}
          <h3 className="text-4xl lg:text-5xl font-bold text-white mb-12">
            Student Success Stories: Real People, Real Triumphs
          </h3>

          {/* Three Success Story Cards */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
            
            {/* Story 1 - Jasmine's Engineering Dream */}
            <div className="border-2 border-orange-500 rounded-2xl p-8 space-y-6 bg-black/50">
              <h4 className="text-2xl font-bold text-white">
                Jasmine's Engineering Dream
              </h4>
              <p className="text-gray-300 text-base leading-relaxed">
                Meet Jasmine: From foster care to a full scholarship in engineering at a top university, guided every step of the way by her dedicated ISF mentor who believed in her potential.
              </p>
              <ul className="space-y-3 text-gray-300 text-base">
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Overcame housing instability during senior year</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Secured full-ride scholarship through mentorship</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Now interning at leading tech company</span>
                </li>
              </ul>
            </div>

            {/* Story 2 - Carlos's Trade School Journey */}
            <div className="border-2 border-orange-500 rounded-2xl p-8 space-y-6 bg-black/50">
              <h4 className="text-2xl font-bold text-white">
                Carlos's Trade School Journey
              </h4>
              <p className="text-gray-300 text-base leading-relaxed">
                Carlos's story: Overcame significant financial hurdles with ISF's Student Success Fund and graduated from trade school with honors, now earning competitive wages as a certified electrician.
              </p>
              <ul className="space-y-3 text-gray-300 text-base">
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Received emergency financial assistance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Completed certification ahead of schedule</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-500 mt-1">•</span>
                  <span>Launched successful independent business</span>
                </li>
              </ul>
            </div>

            {/* Story 3 - Your Story Awaits */}
            <div className="border-2 border-orange-500 rounded-2xl p-8 space-y-6 bg-black/50">
              <h4 className="text-2xl font-bold text-white">
                Your Story Awaits
              </h4>
              <p className="text-gray-300 text-base leading-relaxed">
                These stories prove that with the right support, mentorship, and community behind you, your dreams are absolutely within reach. You could be our next success story.
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* Section 4: Ready to Start Your Journey - CTA Section */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-22 overflow-hidden min-h-[40vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="/Assests/home_page_14.png" 
            alt="Ready to Start Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="max-w-5xl mx-auto relative z-10 text-center">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Ready to Start Your Journey?
          </h2>

          {/* Description */}
          <p className="text-gray-200 text-lg lg:text-xl leading-relaxed mb-10 max-w-4xl mx-auto">
            Don't let another day pass without taking action toward your future. Join hundreds of students who have already transformed their lives through ISF Athena's comprehensive support system.
          </p>

          {/* CTA Button */}
          <button className="px-10 py-4 bg-orange-500 text-white rounded-lg font-semibold text-lg hover:bg-orange-600 transition">
            Apply Now as a Student
          </button>

        </div>
      </section>

      {/* Section 5: For Mentors - Make a Lasting Impact */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
            For Mentors: Make a Lasting Impact in Just One Hour a Week
          </h2>

          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            
            {/* Left Side - Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="/Assests/home_page_90.png" 
                  alt="Mentor Meeting" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="space-y-8">
              
              {/* Subheading */}
              <h3 className="text-3xl lg:text-4xl font-bold text-white">
                Become a Trusted Guide
              </h3>

              {/* Paragraph 1 */}
              <p className="text-gray-300 text-lg leading-relaxed">
                Become a trusted guide for students navigating education and life challenges. Your experience and wisdom can light the path for someone seeking direction.
              </p>

              {/* Paragraph 2 */}
              <p className="text-gray-300 text-lg leading-relaxed">
                ISF mentors commit just 2 hours per month to build meaningful, character-based leadership relationships that transform lives. It's a small investment with extraordinary returns.
              </p>

              {/* Paragraph 3 */}
              <p className="text-gray-300 text-lg leading-relaxed">
                Receive comprehensive training, ongoing support, and get matched with mentees based on shared interests, goals, and values for authentic connection.
              </p>

            </div>

          </div>

        </div>
      </section>

      {/* Section 6: Why Mentor with ISF Athena */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
            Why Mentor with ISF Athena?
          </h2>

          {/* Three Benefits Grid */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-8 mb-8 sm:mb-12">
            
            {/* Benefit 1 - Proven Impact */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 flex-shrink-0">
                  <svg className="w-full h-[60px] text-orange-500 -mt-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Proven Impact
                  </h3>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Mentoring increases student success rates dramatically — your guidance can genuinely change a life trajectory. Research shows mentored students are 55% more likely to enroll in college and 78% more likely to volunteer regularly.
                  </p>
                </div>
              </div>
            </div>

            {/* Benefit 2 - Global Community */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 flex-shrink-0">
                  <svg className="w-full h-[60px] text-orange-500 -mt-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Global Community
                  </h3>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Join a global network aiming for 1 million mentors, supported by secure, AI-powered matching technology that ensures privacy and compatibility. Connect with fellow mentors worldwide.
                  </p>
                </div>
              </div>
            </div>

            {/* Benefit 3 - Personal Growth */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 flex-shrink-0">
                  <svg className="w-full h-[60px] text-orange-500 -mt-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Personal Growth
                  </h3>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Gain personal fulfillment, enhance your leadership skills, and expand your professional network while making a tangible difference in someone's future. Mentors report increased job satisfaction and life purpose.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Banner - Time Investment */}
          <div className="bg-orange-900/80 border-2 border-orange-500 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 flex-shrink-0 mt-0">
                <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                </svg>
              </div>
              <p className="text-white text-lg leading-relaxed">
                <span className="font-bold">Time Investment:</span> Just 2 hours per month — approximately one coffee meeting bi-weekly — can transform a young person's entire future.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Section 7: Be the Light That Guides a Future Leader */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden min-h-[40vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="/Assests/home_page_1.png" 
            alt="Be the Light Background" 
            className="w-full h-[350px] object-cover"
          />
        </div>
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
            Be the Light That Guides a Future Leader
          </h2>

          {/* Description */}
          <p className="text-gray-200 text-lg lg:text-xl leading-relaxed max-w-5xl">
            Every mentor remembers someone who believed in them. Now it's your turn to be that person for someone else. Your wisdom, experience, and encouragement can illuminate possibilities a young person never imagined.
          </p>

        </div>
      </section>

      {/* Section 8: Join Our Mentorship Program Today */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-8">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 text-center">
            Join Our Mentorship Program Today
          </h2>

          {/* Description */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-12 text-center max-w-5xl mx-auto">
            Thousands of students are waiting for someone like you — someone who cares, listens, and guides. The application takes just minutes, but your impact will last a lifetime.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-6 mb-16">
            <button className="px-8 py-4 bg-orange-500 text-white rounded-lg font-semibold text-lg hover:bg-orange-600 transition">
              Become a Mentor
            </button>
            <button className="px-8 py-4 border-2 border-orange-500 text-orange-500 rounded-lg font-semibold text-lg hover:bg-orange-500 hover:text-white transition">
              Learn More About Mentoring
            </button>
          </div>

          {/* Four Steps Grid */}
          <div className="grid md:grid-cols-2 gap-5 sm:gap-8">
            
            {/* Step 1 - Register Online */}
            <div className="space-y-4">
              <div className="text-orange-500 text-xl font-bold">01</div>
              <div className="border-t-2 border-orange-500 pt-4">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Register Online
                </h3>
                <p className="text-gray-300 text-base">
                  Complete our simple application form
                </p>
              </div>
            </div>

            {/* Step 2 - Complete Training */}
            <div className="space-y-4">
              <div className="text-orange-500 text-xl font-bold">02</div>
              <div className="border-t-2 border-orange-500 pt-4">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Complete Training
                </h3>
                <p className="text-gray-300 text-base">
                  Attend orientation and certification workshop
                </p>
              </div>
            </div>

            {/* Step 3 - Get Matched */}
            <div className="space-y-4">
              <div className="text-orange-500 text-xl font-bold">03</div>
              <div className="border-t-2 border-orange-500 pt-4">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Get Matched
                </h3>
                <p className="text-gray-300 text-base">
                  Meet your mentee through AI-powered pairing
                </p>
              </div>
            </div>

            {/* Step 4 - Start Mentoring */}
            <div className="space-y-4">
              <div className="text-orange-500 text-xl font-bold">04</div>
              <div className="border-t-2 border-orange-500 pt-4">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Start Mentoring
                </h3>
                <p className="text-gray-300 text-base">
                  Begin transforming lives together
                </p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Section 9: For Content Providers - Share Your Expertise and Earn */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-gradient-to-br from-cyan-600 to-orange-400">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(60)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-30"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-16">
            For Content Providers: Share Your Expertise and Earn
          </h2>

          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            
            {/* Left Side - Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="/Assests/home_page_91.png" 
                  alt="Content Provider" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="space-y-8">
              
              {/* Subheading */}
              <h3 className="text-3xl lg:text-4xl font-bold text-black">
                Turn Knowledge Into Income
              </h3>

              {/* Paragraph 1 */}
              <p className="text-gray-200 text-lg leading-relaxed">
                Are you a creator, educator, or industry expert with valuable knowledge to share? Your expertise could empower the next generation of leaders.
              </p>

              {/* Paragraph 2 */}
              <p className="text-gray-200 text-lg leading-relaxed">
                Partner with ISF Athena to develop engaging courses, interactive workshops, and transformative resources that empower students and mentors on their journey.
              </p>

              {/* Paragraph 3 */}
              <p className="text-gray-200 text-lg leading-relaxed">
                Earn sustainable revenue while making a meaningful difference in education and leadership development. Build passive income streams while creating lasting impact.
              </p>

            </div>

          </div>

        </div>
      </section>

      {/* Section 10: Why Partner with ISF Athena */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
            Why Partner with ISF Athena?
          </h2>

          {/* Three Arrow Cards */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 mb-10 sm:mb-16">
            
            {/* Card 1 - Motivated Audience */}
            <div className="relative bg-black p-8 space-y-4">
              <div className="w-12 h-12 mb-4">
                <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">
                Motivated Audience
              </h3>
              <p className="text-gray-300 text-base leading-relaxed pr-8">
                Access a highly motivated audience eager for quality content in leadership, career skills, and personal growth. Our students are committed learners seeking transformation.
              </p>
            </div>

            {/* Card 2 - Global Reach */}
            <div className="relative bg-black p-8 space-y-4">
              <div className="w-12 h-12 mb-4">
                <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">
                Global Reach
              </h3>
              <p className="text-gray-300 text-base leading-relaxed px-4">
                Benefit from ISF's trusted brand and expanding global reach to amplify your impact exponentially. We handle marketing so you focus on content creation.
              </p>
            </div>

            {/* Card 3 - Innovation Community */}
            <div className="relative bg-black p-8 space-y-4">
              <div className="w-12 h-12 mb-4">
                <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.87L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">
                Innovation Community
              </h3>
              <p className="text-gray-300 text-base leading-relaxed pl-4">
                Collaborate with a dynamic community that values innovation, excellence, and real-world results. Join creators shaping the future of education.
              </p>
            </div>

          </div>

          {/* Chart Section */}
          <div className="bg-black/50 border-2 border-orange-500 rounded-3xl p-4 sm:p-12 overflow-hidden">
            <div className="max-w-5xl mx-auto">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={[
                    { category: 'Leadership', value: 850 },
                    { category: 'Career Skills', value: 1100 },
                    { category: 'Personal Growth', value: 950 }
                  ]}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis
                    dataKey="category"
                    stroke="#fff"
                    tick={{ fill: '#fff', fontSize: 11 }}
                    interval={0}
                    width={50}
                  />
                  <YAxis
                    stroke="#fff"
                    tick={{ fill: '#fff', fontSize: 11 }}
                    domain={[0, 1200]}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #f97316' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {[0, 1, 2].map((index) => (
                      <Cell key={`cell-${index}`} fill="#f97316" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mt-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500"></div>
                  <span className="text-gray-300 text-sm">Average Enrollment</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-600"></div>
                  <span className="text-gray-300 text-sm">Student Satisfaction</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Section 11: Turn Your Knowledge Into Impact and Income */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            
            {/* Left Side - Content */}
            <div className="space-y-8">
              
              {/* Main Heading */}
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Turn Your Knowledge Into Impact and Income
              </h2>

              {/* Description */}
              <p className="text-gray-300 text-lg leading-relaxed">
                Your expertise has value — not just monetary value, but the power to transform lives. Share what you know with students hungry for knowledge and mentors seeking to enhance their skills.
              </p>

              {/* Three Stats */}
              <div className="space-y-8">
                
                {/* Stat 1 - $5K+ */}
                <div>
                  <div className="text-5xl lg:text-6xl font-bold text-orange-500 mb-2">
                    $5K+
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Average Monthly Earnings
                  </h3>
                  <p className="text-gray-300 text-base">
                    Top content providers' income
                  </p>
                </div>

                {/* Stat 2 - 10K+ */}
                <div>
                  <div className="text-5xl lg:text-6xl font-bold text-orange-500 mb-2">
                    10K+
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Active Learners
                  </h3>
                  <p className="text-gray-300 text-base">
                    Engaged students and mentors
                  </p>
                </div>

                {/* Stat 3 - 4.8 */}
                <div>
                  <div className="text-5xl lg:text-6xl font-bold text-orange-500 mb-2">
                    4.8
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Average Rating
                  </h3>
                  <p className="text-gray-300 text-base">
                    Student satisfaction score
                  </p>
                </div>

              </div>

            </div>

            {/* Right Side - Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="/Assests/home_page_92.png" 
                  alt="Turn Knowledge Into Income" 
                  className="w-full h-[700px] object-cover"
                />
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Section 12: Become a Content Provider */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 sm:py-20 bg-black">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-orange-500 mb-8 sm:mb-16 text-left">
            Become a Content Provider
          </h2>

          {/* Description */}
          <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-8 sm:mb-12">
            Join a select group of educators and experts who are shaping the future of education. Submit your proposal today and start earning while making a difference.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 sm:gap-6 mb-12 sm:mb-20">
            <button className="px-6 sm:px-8 py-4 bg-orange-500 text-black rounded-lg font-semibold text-lg hover:bg-orange-600 transition">
              Partner with ISF Athena
            </button>
            <button className="px-6 sm:px-8 py-4 border-2 border-orange-500 text-orange-500 rounded-lg font-semibold text-lg hover:bg-orange-500 hover:text-white transition">
              View Content Guidelines
            </button>
          </div>

          {/* How It Works Section */}
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-orange-500 mb-10 sm:mb-16 text-left">
            How It Works: Simple Steps to Join & Earn
          </h3>

          {/* Timeline Layout */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-orange-500 hidden md:block"></div>

            <div className="space-y-12">
              
              {/* Step 1 - Students: Apply & Connect */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="md:text-right space-y-4">
                  <h4 className="text-2xl font-bold text-white">
                    Students: Apply & Connect
                  </h4>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Apply online, get matched with a compatible mentor based on your interests and goals, access comprehensive financial support, and start your educational journey with confidence.
                  </p>
                </div>
                <div className="flex items-center justify-center md:justify-start">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-black font-bold text-2xl border-4 border-black">
                    1
                  </div>
                </div>
              </div>

              {/* Step 2 - Mentors: Register & Guide */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="md:order-2 space-y-4">
                  <h4 className="text-2xl font-bold text-white">
                    Mentors: Register & Guide
                  </h4>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Register through our secure portal, complete orientation and certification training, get matched with your ideal mentee, and dedicate just a few hours monthly to transformation.
                  </p>
                </div>
                <div className="md:order-1 flex items-center justify-center md:justify-end">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-black font-bold text-2xl border-4 border-black">
                    2
                  </div>
                </div>
              </div>

              {/* Step 3 - Content Providers: Create & Earn */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="md:text-right space-y-4">
                  <h4 className="text-2xl font-bold text-white">
                    Content Providers: Create & Earn
                  </h4>
                  <p className="text-gray-300 text-base leading-relaxed">
                    Submit your course proposal with curriculum outline, create engaging content using our guidelines and support, and earn revenue from enrollments with transparent royalty structures.
                  </p>
                </div>
                <div className="flex items-center justify-center md:justify-start">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-black font-bold text-2xl border-4 border-black">
                    3
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Bottom Info Banner */}
          <div className="mt-16 bg-orange-900/80 border-2 border-orange-500 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 flex-shrink-0 mt-1">
                <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              </div>
              <p className="text-white text-lg leading-relaxed">
                <span className="font-bold">Getting Started is Easy:</span> Most applications are reviewed within 48 hours, and you can begin your journey within one week of approval.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Section 13: The ISF Athena Advantage */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 bg-gradient-to-br from-cyan-400/80 to-orange-300/80">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(60)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-30"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Content Card */}
          <div className="bg-gray-800 rounded-3xl p-12 lg:p-16">
            
            {/* Main Heading */}
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-12">
              The ISF Athena Advantage: Support, Security, and Community
            </h2>

            {/* Three Features with Images */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              
              {/* Feature 1 - AI-Powered Security */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8">
                    <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    AI-Powered Security
                  </h3>
                </div>
                <div className="rounded-2xl overflow-hidden mb-4">
                  <img 
                    src="/Assests/home_page_93.png" 
                    alt="AI Security" 
                    className="w-full h-48 object-cover"
                  />
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                  AI-powered secure matching ensures complete privacy and the best possible mentor-mentee fit based on compatibility factors, interests, and goals.
                </p>
              </div>

              {/* Feature 2 - Ongoing Training */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8">
                    <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Ongoing Training
                  </h3>
                </div>
                <div className="rounded-2xl overflow-hidden mb-4">
                  <img 
                    src="/Assests/home_page_94.png" 
                    alt="Training Workshop" 
                    className="w-full h-48 object-cover"
                  />
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                  Ongoing training workshops and leadership retreats for students and mentors ensure continuous growth and skill development throughout your journey.
                </p>
              </div>

              {/* Feature 3 - Vibrant Network */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8">
                    <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12.75c1.63 0 3.07.39 4.24.9 1.08.48 1.76 1.56 1.76 2.73V18H6v-1.61c0-1.18.68-2.26 1.76-2.73 1.17-.52 2.61-.91 4.24-.91zM4 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1.13 1.1c-.37-.06-.74-.1-1.13-.1-.99 0-1.93.21-2.78.58C.48 14.9 0 15.62 0 16.43V18h4.5v-1.61c0-.83.23-1.61.63-2.29zM20 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4 3.43c0-.81-.48-1.53-1.22-1.85-.85-.37-1.79-.58-2.78-.58-.39 0-.76.04-1.13.1.4.68.63 1.46.63 2.29V18H24v-1.57zM12 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Vibrant Network
                  </h3>
                </div>
                <div className="rounded-2xl overflow-hidden mb-4">
                  <img 
                    src="/Assests/home_page_95.png" 
                    alt="Community Network" 
                    className="w-full h-48 object-cover"
                  />
                </div>
                <p className="text-gray-300 text-base leading-relaxed">
                  A vibrant, supportive network committed to lifelong learning and success connects you with like-minded individuals pursuing excellence and impact.
                </p>
              </div>

            </div>

            {/* Three Circular Stats */}
            <div className="grid md:grid-cols-3 gap-12">
              
              {/* Stat 1 - 95% Match Success Rate */}
              <div className="text-center space-y-4">
                <div className="relative inline-block">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle cx="80" cy="80" r="70" stroke="#374151" strokeWidth="12" fill="none" />
                    <circle 
                      cx="80" 
                      cy="80" 
                      r="70" 
                      stroke="#f97316" 
                      strokeWidth="12" 
                      fill="none"
                      strokeDasharray="440"
                      strokeDashoffset="22"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">95%</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-white">
                  Match Success Rate
                </h4>
                <p className="text-gray-300 text-base">
                  Mentors and students report excellent compatibility
                </p>
              </div>

              {/* Stat 2 - 89% Student Retention */}
              <div className="text-center space-y-4">
                <div className="relative inline-block">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle cx="80" cy="80" r="70" stroke="#374151" strokeWidth="12" fill="none" />
                    <circle 
                      cx="80" 
                      cy="80" 
                      r="70" 
                      stroke="#f97316" 
                      strokeWidth="12" 
                      fill="none"
                      strokeDasharray="440"
                      strokeDashoffset="48"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">89%</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-white">
                  Student Retention
                </h4>
                <p className="text-gray-300 text-base">
                  Students stay enrolled and engaged
                </p>
              </div>

              {/* Stat 3 - 92% Satisfaction Score */}
              <div className="text-center space-y-4">
                <div className="relative inline-block">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle cx="80" cy="80" r="70" stroke="#374151" strokeWidth="12" fill="none" />
                    <circle 
                      cx="80" 
                      cy="80" 
                      r="70" 
                      stroke="#f97316" 
                      strokeWidth="12" 
                      fill="none"
                      strokeDasharray="440"
                      strokeDashoffset="35"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">92%</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-white">
                  Satisfaction Score
                </h4>
                <p className="text-gray-300 text-base">
                  Overall community satisfaction rating
                </p>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* Section 14: Testimonials - Voices from Our Community */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 ">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(60)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-30"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
            Testimonials: Voices from Our Community
          </h2>

          {/* Three Testimonial Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Testimonial 1 - Aisha, ISF Student */}
            <div className="space-y-6">
              {/* Quote Card */}
              <div className="bg-cyan-100 border-4 border-orange-500 rounded-2xl p-6 relative">
                {/* Top Quote Mark */}
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  "
                </div>
                {/* Bottom Quote Mark */}
                <div className="absolute -bottom-3 -right-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  "
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-black mb-2">
                    Aisha, ISF Student
                  </h3>
                </div>
                <p className="text-black text-base leading-relaxed">
                  "My mentor helped me navigate college applications and life decisions — I couldn't have done it alone. She believed in me when I didn't believe in myself, and now I'm thriving at my dream university."
                </p>
              </div>
              {/* Image */}
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src="/Assests/home_page_96.png" 
                  alt="Aisha" 
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>

            {/* Testimonial 2 - David, ISF Mentor */}
            <div className="space-y-6">
              {/* Quote Card */}
              <div className="bg-cyan-100 border-4 border-orange-500 rounded-2xl p-6 relative">
                {/* Top Quote Mark */}
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  "
                </div>
                {/* Bottom Quote Mark */}
                <div className="absolute -bottom-3 -right-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  "
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-black mb-2">
                    David, ISF Mentor
                  </h3>
                </div>
                <p className="text-black text-base leading-relaxed">
                  "Mentoring with ISF has been one of the most rewarding experiences of my career. Watching my mentee grow from uncertain to confident has reminded me why giving back matters so profoundly."
                </p>
              </div>
              {/* Image */}
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src="/Assests/home_page_97.png" 
                  alt="David" 
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>

            {/* Testimonial 3 - Sarah, Content Provider */}
            <div className="space-y-6">
              {/* Quote Card */}
              <div className="bg-cyan-100 border-4 border-orange-500 rounded-2xl p-6 relative">
                {/* Top Quote Mark */}
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  "
                </div>
                {/* Bottom Quote Mark */}
                <div className="absolute -bottom-3 -right-3 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  "
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-black mb-2">
                    Sarah, Content Provider
                  </h3>
                </div>
                <p className="text-black text-base leading-relaxed">
                  "Partnering with ISF allowed me to share my expertise with students who truly value learning, and I've grown my audience significantly. The revenue stream supports my creative work while making real impact."
                </p>
              </div>
              {/* Image */}
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src="/Assests/home_page_98.png" 
                  alt="Sarah" 
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Section 15: Frequently Asked Questions */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-12 bg-orange-500/60">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(80)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-50"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          
          {/* Main Heading */}
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
            Frequently Asked Questions
          </h2>

          {/* FAQ Grid - 2x2 */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            
            {/* FAQ 1 */}
            <div className="border-2 border-gray-200 rounded-2xl p-8 space-y-4 bg-black/50">
              <h3 className="text-xl font-bold text-white">
                Who is eligible to join as a student or mentor?
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                <span className="font-semibold text-white">Students:</span> High school seniors or graduates with foster care experience pursuing college or trade certification.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                <span className="font-semibold text-white">Mentors:</span> Adults 21+ with clean background checks and commitment to monthly engagement.
              </p>
            </div>

            {/* FAQ 2 */}
            <div className="border-2 border-gray-200 rounded-2xl p-8 space-y-4 bg-black/50">
              <h3 className="text-xl font-bold text-white">
                What is the time commitment for mentors?
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Mentors commit to just 2 hours per month — typically one meeting or check-in every two weeks. Flexible scheduling accommodates busy professionals.
              </p>
            </div>

            {/* FAQ 3 */}
            <div className="border-2 border-gray-200 rounded-2xl p-8 space-y-4 bg-black/50">
              <h3 className="text-xl font-bold text-white">
                How do content providers get paid?
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                Content providers earn revenue through enrollment-based royalties with transparent payment structures. Payments are processed monthly with detailed analytics on course performance.
              </p>
            </div>

            {/* FAQ 4 */}
            <div className="border-2 border-gray-200 rounded-2xl p-8 space-y-4 bg-black/50">
              <h3 className="text-xl font-bold text-white">
                How does ISF ensure safety and privacy?
              </h3>
              <p className="text-gray-300 text-base leading-relaxed">
                We use AI-powered secure matching, require comprehensive background checks, maintain strict privacy protocols, and provide ongoing monitoring and support for all relationships.
              </p>
            </div>

          </div>

          {/* Bottom Contact Info */}
          <div className="text-center">
            <p className="text-gray-300 text-lg">
              Have more questions? Contact our support team at{' '}
              <a 
                href="mailto:support@isfacademy.org" 
                className="text-orange-500 font-semibold hover:text-orange-400 transition"
              >
                support@isfacademy.org
              </a>
              {' '}or call{' '}
              <span className="text-orange-500 font-semibold">1-800-ISF-HELP</span>
              {' '}for immediate assistance.
            </p>
          </div>

        </div>
      </section>

      {/* Section 16: Join the Movement */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-20 ">
        {/* Background stars effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(60)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white rounded-full opacity-30"
              style={{
                width: Math.random() * 2 + 1 + 'px',
                height: Math.random() * 2 + 1 + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Content Card */}
          <div className="bg-gradient-to-br from-indigo-400 to-orange-300 rounded-3xl p-12 lg:p-16">
            
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
              
              {/* Left Side - Image */}
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden">
                  <img 
                    src="/Assests/home_page_99.png" 
                    alt="Join the Movement" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="space-y-8">
                
                {/* Main Heading */}
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                  Join the Movement: Empower Futures, Earn Rewards, and Build Community
                </h2>

                {/* Paragraph 1 */}
                <p className="text-gray-300 text-lg leading-relaxed">
                  Whether you're a student seeking guidance, a mentor ready to give back, or a content provider eager to share expertise, ISF Athena offers a unique opportunity to grow and make meaningful impact.
                </p>

                {/* Paragraph 2 */}
                <p className="text-gray-300 text-lg leading-relaxed">
                  Together, we create sustainable pathways to success for foster youth and beyond, building a community where everyone thrives through education, mentorship, and shared purpose.
                </p>

                {/* Paragraph 3 */}
                <p className="text-gray-300 text-lg leading-relaxed">
                  Don't wait another moment — your journey to making an impact and earning rewards starts right now. The lives you'll touch are waiting.
                </p>

              </div>

            </div>

            {/* Three Stats Row */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-12 mt-8 sm:mt-16 pt-8 sm:pt-12 border-t-2 border-gray-600">
              
              {/* Stat 1 - 5K+ Active Members */}
              <div className="text-center space-y-3">
                <div className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white">
                  5K+
                </div>
                <h3 className="text-2xl font-bold text-white">
                  Active Members
                </h3>
                <p className="text-gray-200 text-base">
                  Students, mentors, and providers
                </p>
              </div>

              {/* Stat 2 - 15K+ Success Stories */}
              <div className="text-center space-y-3">
                <div className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white">
                  15K+
                </div>
                <h3 className="text-2xl font-bold text-white">
                  Success Stories
                </h3>
                <p className="text-gray-200 text-base">
                  Lives transformed through ISF
                </p>
              </div>

              {/* Stat 3 - 1M Our Goal */}
              <div className="text-center space-y-3">
                <div className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white">
                  1M
                </div>
                <h3 className="text-2xl font-bold text-white">
                  Our Goal
                </h3>
                <p className="text-gray-200 text-base">
                  Mentors worldwide by 2030
                </p>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* Section 17: Get Started Today - Final CTA */}
      <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden min-h-[60vh] sm:min-h-[80vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="/Assests/home_page_86.png" 
            alt="Get Started Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-start">
            
            {/* Left Side - Main CTA */}
            <div className="space-y-8">
              
              {/* Main Heading */}
              <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                Get Started Today!
              </h2>

              {/* Description */}
              <p className="text-gray-200 text-lg lg:text-xl leading-relaxed max-w-2xl">
                Your journey begins with a single click. Choose your path and join thousands who are already transforming lives through ISF Athena. Together, we're building a brighter future for foster youth and creating opportunities for everyone to learn, grow, and earn.
              </p>

              {/* Three CTA Buttons */}
              <div className="flex flex-col gap-4 max-w-md">
                <button className="w-full px-8 py-4 bg-orange-500 text-black rounded-lg font-semibold text-lg hover:bg-orange-600 transition">
                  Apply as Student
                </button>
                <button className="w-full px-8 py-4 bg-orange-500 text-black rounded-lg font-semibold text-lg hover:bg-orange-600 transition">
                  Become a Mentor
                </button>
                <button className="w-full px-8 py-4 bg-orange-500 text-black rounded-lg font-semibold text-lg hover:bg-orange-600 transition">
                  Partner as Content Provider
                </button>
              </div>

            </div>

            {/* Right Side - Contact Info */}
            <div className="space-y-12">
              
              {/* Questions Section */}
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-white">
                  Questions? We're Here to Help
                </h3>
                
                <div className="space-y-4">
                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex-shrink-0 mt-1">
                      <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base">
                        <span className="font-semibold text-white">Email:</span>{' '}
                        <a 
                          href="isfanalyticaanalytica@gmail.com" 
                          className="text-orange-500 hover:text-orange-400 transition"
                        >
                          isfanalyticaanalytica@gmail.com
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex-shrink-0 mt-1">
                      <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base">
                        <span className="font-semibold text-white">Phone:</span>{' '}
                        <span className="text-orange-500">+91-9890906048</span>
                        {' '}(9890906048)
                      </p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4">
                    <div className="w-6 h-6 flex-shrink-0 mt-1">
                      <svg className="w-full h-full text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base">
                        <span className="font-semibold text-white">Available:</span> Monday to Friday, 10 AM to 7 AM
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connect With Us Section */}
              <div className="space-y-6 pt-8 border-t-2 border-gray-600">
                <h3 className="text-3xl font-bold text-white">
                  Connect With Us
                </h3>
                <p className="text-gray-300 text-base leading-relaxed">
                  Follow our journey on social media and stay updated on success stories, opportunities, and community events.
                </p>
                <p className="text-gray-300 text-base">
                  <span className="font-semibold text-white">@ISFAcademy</span>{' '}
                  <span className="text-orange-500">on all platforms</span>
                </p>
              </div>

            </div>

          </div>

        </div>
      </section>

    </div>
  );
}

export default JoinAndEarn;